import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchWithCsrf } from "@/lib/api";
import type { Jam, CreateJamData, UpdateJamData } from "@/types/jams";
import type { ApiSuccess, ApiError } from "@/types/api";

export const JAMS_QUERY_KEY = ["jams"] as const;

interface JamsResponse {
  jams: Jam[];
}

interface JamResponse {
  jam: Jam;
}

export function useJams() {
  return useQuery<Jam[]>({
    queryKey: JAMS_QUERY_KEY,
    queryFn: async () => {
      const result = await fetchWithCsrf<ApiSuccess<JamsResponse>>("/api/jams");
      return result.data.jams;
    },
  });
}

export function useJam(id: string | undefined) {
  return useQuery<Jam>({
    queryKey: [...JAMS_QUERY_KEY, id],
    queryFn: async () => {
      const result = await fetchWithCsrf<ApiSuccess<JamResponse>>(
        `/api/jams/${id}`
      );
      return result.data.jam;
    },
    enabled: !!id,
  });
}

export function useCreateJam() {
  const queryClient = useQueryClient();

  return useMutation<ApiSuccess<JamResponse>, ApiError, CreateJamData>({
    mutationFn: async (data) => {
      return fetchWithCsrf("/api/jams", {
        method: "POST",
        body: JSON.stringify({ jam: data }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: JAMS_QUERY_KEY });
    },
  });
}

export function useJoinJam() {
  const queryClient = useQueryClient();

  return useMutation<ApiSuccess<JamResponse>, ApiError, string>({
    mutationFn: async (code) => {
      return fetchWithCsrf("/api/jams/join", {
        method: "POST",
        body: JSON.stringify({ code }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: JAMS_QUERY_KEY });
    },
  });
}

export function useRegenerateJoinCode() {
  const queryClient = useQueryClient();

  return useMutation<ApiSuccess<JamResponse>, ApiError, string>({
    mutationFn: async (id) => {
      return fetchWithCsrf(`/api/jams/${id}/regenerate_join_code`, {
        method: "POST",
      });
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: JAMS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: [...JAMS_QUERY_KEY, id] });
    },
  });
}

export function useUpdateJam() {
  const queryClient = useQueryClient();

  return useMutation<
    ApiSuccess<JamResponse>,
    ApiError,
    { id: string; data: UpdateJamData }
  >({
    mutationFn: async ({ id, data }) => {
      return fetchWithCsrf(`/api/jams/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ jam: data }),
      });
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: JAMS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: [...JAMS_QUERY_KEY, id] });
    },
  });
}

export function useDeleteJam() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, string>({
    mutationFn: async (id) => {
      await fetchWithCsrf(`/api/jams/${id}`, { method: "DELETE" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: JAMS_QUERY_KEY });
    },
  });
}
